import { Icon, MessagePanel, ProgressSpinner } from '@dittolive/anvil'
import { CheckCircleIcon, InfoIcon } from '@phosphor-icons/react'
import React from 'react'

import { ConnectorState, ConnectorStatus } from '../../../types/mongoConnector'

type Props = {
  status: ConnectorStatus | null
  isLoading?: boolean
}

export default function MongoConnectorStatus({ status, isLoading }: Props) {
  if (isLoading || status?.state === 'exiting') {
    return null
  }

  // If the status doesn't exist, we assume the connector is pending creation.
  // We can also make the assumption the state is not exiting because of the above check
  const state = (status?.state || 'pending') as Exclude<
    ConnectorState,
    'exiting'
  >

  const renderBanner = () => {
    if (state === 'running') return null

    return (
      <MessagePanel
        className="mb-3 rounded-lg"
        variant={state === 'pending' ? 'info' : 'danger'}
        message={
          state === 'error'
            ? CONNECTOR_BANNER_TEXT[state](status?.errorMessages || [])
            : CONNECTOR_BANNER_TEXT[state]
        }
      />
    )
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center space-x-2 pb-3">
        {CONNECTOR_ICON[state]}
        <span className="text-foreground-normal text-base font-medium leading-5">
          {CONNECTOR_HEADER_TEXT[state]}
        </span>
      </div>

      {renderBanner()}
    </div>
  )
}

const CONNECTOR_ICON: Record<
  Exclude<ConnectorState, 'exiting'>,
  React.ReactNode
> = {
  pending: <ProgressSpinner className="h-4 w-4" />,
  running: (
    <Icon
      svg={<CheckCircleIcon weight="fill" />}
      className="text-fill-success size-4"
    />
  ),
  error: (
    <Icon
      svg={<InfoIcon weight="fill" />}
      className="text-fill-critical size-4"
    />
  ),
}

const CONNECTOR_HEADER_TEXT: Record<
  Exclude<ConnectorState, 'exiting'>,
  string
> = {
  pending: 'Connecting...',
  running: 'Connected',
  error: 'Failed to connect',
}

const CONNECTOR_BANNER_TEXT = {
  pending:
    "We're just setting up your connection. This might take a few minutes. You can leave this page and return to see the status of your MongoDB connection.",
  error: (errors?: string[]) =>
    errors?.length
      ? `We couldn't sync your MongoDB collections: ${errors.join(', ')}`
      : "We couldn't sync your MongoDB collections.",
} as const
